<template>
  <MarketingPageTemplate
    :title="data.title || 'Social Media'"
    :subtitle="data.subtitle || 'Beiträge, Reichweite und Interaktionen nach Plattform'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade Social-Media-Daten…"
    @retry="reload"
  >
    <template #channel-filters>
      <div class="fpair">
        <span class="flabel">Plattform</span>
        <SingleDropdown
          label="Plattform"
          :options="platformOptions"
          :model-value="platform"
          @update:model-value="(value) => setExtra({ platform: value })"
        />
      </div>
      <div class="fpair">
        <span class="flabel">Content-Typ</span>
        <SingleDropdown
          label="Content-Typ"
          :options="contentOptions"
          :model-value="contentType"
          @update:model-value="(value) => setExtra({ content_type: value })"
        />
      </div>
      <div class="fpair">
        <span class="flabel">Maßnahmentyp</span>
        <SingleDropdown
          label="Maßnahmentyp"
          :options="spendOptions"
          :model-value="spendType"
          @update:model-value="(value) => setExtra({ spend_type: value })"
        />
      </div>
    </template>

    <template #kpis>
      <MarketingKpis :columns="4">
        <template v-for="card in kpis" :key="card.key">
          <!-- UX: Zusammensetzung der Interaktionen als Meta-Zeile + Tooltip an der Kachel
               statt als dauerhaft offener Kasten mit verwaistem "i". -->
          <KpiTile
            v-if="card.key === 'engagements'"
            :card="card"
            :meta="compositionMeta"
            :tooltip-items="compositionItems"
          />
          <KpiTile v-else :card="card" />
        </template>
      </MarketingKpis>
    </template>

    <template #main>
      <div class="mb-split">
        <!-- UX: Plattformfarben – Kanal- und Schaltungstypfarben sind für ihre Bedeutung reserviert. -->
        <DashboardSection :title="`${visibilityLabel} nach Plattform`" :empty="!platformNames.length">
          <MarketingChart
            :categories="platformNames"
            :series="visibilitySeries"
            :colors="platformColors"
            distributed
            x-title="Plattform"
          />
        </DashboardSection>
        <DashboardSection title="Interaktionen nach Plattform" :empty="!platformNames.length">
          <MarketingChart
            :categories="platformNames"
            :series="engagementSeries"
            :colors="platformColors"
            distributed
            x-title="Plattform"
          />
        </DashboardSection>
      </div>
    </template>

    <DashboardSection title="Organisch und bezahlt" :empty="!spendRows.length">
      <TableScroll>
        <table class="jr-list">
          <thead>
            <tr>
              <th scope="col">Maßnahmentyp</th>
              <th scope="col" class="num">Beiträge</th>
              <th scope="col" class="num">{{ visibilityLabel }}</th>
              <th scope="col" class="num">Interaktionen</th>
              <th scope="col" class="num">Engagement-Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in activeSpendRows" :key="row.code">
              <td>{{ row.name }}</td>
              <td class="num">{{ formatNumber(row.posts) }}</td>
              <td class="num">
                {{ formatNumber(row.visibility) }}
                <span v-if="row.visibility_label && row.visibility_label !== visibilityLabel" class="mb-unit-hint">
                  {{ row.visibility_label }}
                </span>
              </td>
              <td class="num">{{ formatNumber(row.engagements) }}</td>
              <td class="num">{{ formatPercent(row.engagement_rate) }}</td>
            </tr>
            <!-- UX: Kategorien ohne Beiträge in einer ausgegrauten Zeile statt 3 Nullzeilen mit "Reichweite". -->
            <tr v-if="emptySpendRows.length" class="mb-row-muted">
              <td colspan="5">
                {{ emptySpendRows.length }} {{ emptySpendRows.length === 1 ? 'Kategorie' : 'Kategorien' }} ohne Beiträge:
                {{ emptySpendRows.map((row) => row.name).join(', ') }}
              </td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>

    <DashboardSection title="Beiträge" :empty="!tableRows.length">
      <TableScroll>
        <table class="jr-list">
          <thead>
            <tr>
              <th scope="col">Beitrag</th>
              <th scope="col">Plattform</th>
              <th scope="col">Typ</th>
              <!-- UX: Leere Spalte "Content" ausblenden, bis Daten vorhanden sind. -->
              <th v-if="showContent" scope="col">Content</th>
              <th scope="col">Datum</th>
              <th scope="col" class="num">{{ visibilityLabel }}</th>
              <th scope="col" class="num">Interaktionen</th>
              <th scope="col" class="num">Engagement-Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableRows" :key="row.id">
              <td>
                <span class="mb-cell-name" :title="row.name">
                  {{ row.name }}
                  <InfoTooltip
                    title="Zusammensetzung der Interaktionen"
                    :items="compositionItemsFor(row.composition)"
                  />
                </span>
              </td>
              <td>
                <span class="mb-platform">
                  <i class="mb-platform__dot" :style="{ background: platformColor(row.platform) }" aria-hidden="true" />
                  {{ row.platform }}
                </span>
              </td>
              <td>{{ row.spend_type_label }}</td>
              <td v-if="showContent">{{ row.content_type || '–' }}</td>
              <td>{{ formatDate(row.published_at) }}</td>
              <td class="num">
                {{ formatNumber(row.visibility) }}
                <span v-if="row.visibility_label && row.visibility_label !== visibilityLabel" class="mb-unit-hint">
                  {{ row.visibility_label }}
                </span>
              </td>
              <td class="num">{{ formatNumber(row.engagements) }}</td>
              <td class="num">{{ formatPercent(row.engagement_rate) }}</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import SingleDropdown from '@/features/dashboard/components/SingleDropdown.vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import KpiTile from '../components/KpiTile.vue';
import MarketingChart from '../components/MarketingChart.vue';
import InfoTooltip from '../components/InfoTooltip.vue';
import TableScroll from '../components/TableScroll.vue';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import { platformColor } from '../composables/designTokens';
import { columnHasData } from '../composables/tableUtils';
import { formatDate, formatNumber, formatPercent } from '../composables/formatters';

const props = defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

const { extraValue, setExtra } = useMarketingFilters();
const platform = computed(() => extraValue('platform', ''));
const contentType = computed(() => extraValue('content_type', ''));
const spendType = computed(() => extraValue('spend_type', ''));
const extraParams = computed(() => ({
  platform: platform.value,
  content_type: contentType.value,
  spend_type: spendType.value
}));
const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/social/', extraParams);

const platformOptions = computed(() => [
  { value: '', label: 'Alle Plattformen' },
  ...(props.filterOptions.platforms || []).map((item) => ({ value: item, label: item }))
]);
const contentOptions = computed(() => [
  { value: '', label: 'Alle Content-Typen' },
  ...(props.filterOptions.content_types || []).map((item) => ({ value: item, label: item }))
]);
const spendOptions = computed(() => [
  { value: '', label: 'Alle Maßnahmentypen' },
  ...(props.filterOptions.spend_types || []).map((item) => ({ value: item.code, label: item.name }))
]);

const visibilityLabel = computed(() => data.value.visibility_label || 'Impressions');
const tableRows = computed(() => data.value.table || []);
const spendRows = computed(() => data.value.by_spend_type || []);
const activeSpendRows = computed(() => spendRows.value.filter((row) => row.posts));
const emptySpendRows = computed(() => spendRows.value.filter((row) => !row.posts));
const showContent = computed(() => columnHasData(tableRows.value, 'content_type'));

const platformNames = computed(() => (data.value.series?.by_platform || []).map((item) => item.platform));
const platformColors = computed(() => platformNames.value.map((name) => platformColor(name)));
const visibilitySeries = computed(() => [
  { name: visibilityLabel.value, data: (data.value.series?.by_platform || []).map((item) => item.visibility) }
]);
const engagementSeries = computed(() => [
  { name: 'Interaktionen', data: (data.value.series?.by_platform || []).map((item) => item.engagements) }
]);

const kpis = computed(() => {
  const cards = [...(data.value.kpis || [])];
  const posts = tableRows.value.length;
  if (!cards.some((item) => item.key === 'posts')) {
    cards.unshift({ key: 'posts', label: 'Beiträge', value: posts, format: 'number' });
  }
  return cards;
});

const COMPOSITION = [
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Kommentare' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'clicks', label: 'Klicks' }
];

function compositionItemsFor(composition) {
  if (!composition) return [];
  return COMPOSITION.map((item) => ({ label: item.label, value: formatNumber(composition[item.key]) }));
}

const compositionItems = computed(() => compositionItemsFor(data.value.composition));

const compositionMeta = computed(() => {
  const composition = data.value.composition;
  if (!composition) return '';
  return COMPOSITION.map((item) => `${formatNumber(composition[item.key])} ${item.label}`).join(' · ');
});
</script>
